import React, { useCallback, useMemo } from 'react';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import posthog from 'posthog-js';
import { EmptorTag } from '../emptor_tag/EmptorTag';
import WarrantyDisplay from '../common/WarrantyDisplay';
import {
	checkIsWarrantyActive,
	getWarrantyStatus,
	nullSafeGet,
	nullSafeGetOrElse,
	stopDefaultEvents,
} from '../../utils/DataAccessUtils';
import { Button, Typography } from 'antd';
import OfflineTag from '../common/OfflineTag';
import QRCode from 'qrcode.react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import { getEffectiveAge } from '../../utils/assetUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import { BUYER_ONLY_NO_STORE_ASSOCIATE } from '../roles/roles';
import AssetIdDisplay from './AssetIdDisplay';
import renderWithHighlight from '../RenderHighlight';
import OWButtonWithSupplierManagedLock from '../common/OWButtonWithSupplierManagedLock';
import DateTimeHover from '../date_time_component/DateTime';
import { ArrowRightOutlined, EnterOutlined } from '@ant-design/icons';

require('./AssetRowDisplay.less');
const { Text } = Typography;

const DEFAULT_BACK_LINK_TEXT = 'Back to Assets';

const HiddenQRCode = () => (
	<div className="invisible">
		<QRCode size={64} value={''} />
	</div>
);

const AssetWithQRCodeDisplay: React.FC<any> = ({
	asset,
	companyConfig,
	userType,
	currentUser,
	history,
	location,
	disableActions,
}) => {
	const warrantyStatus = getWarrantyStatus(nullSafeGet('warranties.0', asset));
	const partWarranty = nullSafeGet('partWarranty', asset);
	const highlight = nullSafeGet('highlight', asset);
	const parentAssetName = nullSafeGet('parent.name', asset);
	const parentAssetId = nullSafeGet('parent.id', asset) || nullSafeGet('parentId', asset);
	const effectiveAge = getEffectiveAge(asset);

	const removeAssetNumberToQRCodeAssociation = useMemo(
		() =>
			userType === ROLE_TYPES.SUPPLIER
				? nullSafeGetOrElse(
						'facility.privateBuyerCompanySettings.config.assetConfig.removeAssetNumberToQRCodeAssociation',
						currentUser,
						false
				  )
				: nullSafeGetOrElse(
						'config.assetConfig.removeAssetNumberToQRCodeAssociation',
						companyConfig,
						false
				  ),
		[companyConfig, currentUser, userType]
	);

	const newWorkOrderLink =
		userType === ROLE_TYPES.SUPPLIER
			? 'newWorkOrder'
			: currentUser.roles &&
			  currentUser.roles.some((role) => BUYER_ONLY_NO_STORE_ASSOCIATE.has(role))
			? 'newWorkOrder'
			: 'requestService';

	const redirectTo = useCallback(
		(path) => (e) => {
			stopDefaultEvents(e);
			history.push(getLinkWIthBackLinkParams(location, DEFAULT_BACK_LINK_TEXT, path));
		},
		[history, location]
	);

	return (
		<div className="assetRowDisplay group">
			<div className="flex flex-col gap-3">
				<div className="flex flex-col">
					{parentAssetName ? (
						<div className="flex flex-row items-center text-gray-500">
							<EnterOutlined className="mr-1" style={{ transform: 'scaleX(-1)' }} />
							<span>
								Sub Asset of{' '}
								{parentAssetId ? (
									<Link
										to={getLinkWIthBackLinkParams(
											location,
											DEFAULT_BACK_LINK_TEXT,
											`/${userType}/assets/detail/${parentAssetId}/details`
										)}
										onClick={(e) => e.stopPropagation()}
									>
										{parentAssetName}
									</Link>
								) : (
									parentAssetName
								)}
							</span>
						</div>
					) : null}
					<div className="assetRowDisplay__rowTitle flex flex-row items-center">
						<Ellipsis tooltip={true} lines={1}>
							{renderWithHighlight(highlight, 'name', asset.name, false, 'highlights')}
						</Ellipsis>
					</div>
				</div>
				<div className="flex flex-row flex-wrap gap-y-1 text-gray-500">
					<AssetIdDisplay asset={asset} highlight={highlight} />
					{asset.downtimeStatus === 'offline' ? (
						<div className="mr-4 whitespace-nowrap">
							<OfflineTag />
						</div>
					) : null}
					{checkIsWarrantyActive(nullSafeGet('warranties.0', asset)) ? (
						<div className="mr-4 whitespace-nowrap">
							{WarrantyDisplay(warrantyStatus, 'Service')}
						</div>
					) : null}
					{checkIsWarrantyActive(partWarranty) ? (
						<div className="mr-4 whitespace-nowrap">
							{WarrantyDisplay(getWarrantyStatus(partWarranty), 'Part')}
						</div>
					) : null}
					{asset.manufacturer ? (
						<div className="mr-4 whitespace-nowrap">
							Manufacturer{' '}
							{renderWithHighlight(
								highlight,
								'manufacturer',
								asset.manufacturer,
								false,
								'highlights'
							)}
						</div>
					) : null}

					{asset.installDate ? (
						<div className="mr-4 whitespace-nowrap">
							Install Date{' '}
							<DateTimeHover
								timestamp={nullSafeGet('installDate', asset)}
								showDate={true}
								showTime={false}
								timeZone={nullSafeGet('location.timeZoneId', asset)}
							/>
						</div>
					) : null}

					{asset.modelNumber ? (
						<div className="mr-4 whitespace-nowrap">
							Model Number{' '}
							{renderWithHighlight(
								highlight,
								'modelNumber',
								asset.modelNumber,
								false,
								'highlights'
							)}
						</div>
					) : null}
					{asset.isActive ? null : (
						<div className="mr-4 whitespace-nowrap">
							<Text className={'mr-1'} type={'danger'}>
								Inactive
							</Text>
						</div>
					)}
				</div>
				<div className="flex items-center text-gray-500">
					<div className="mr-4 flex flex-shrink flex-row items-center gap-2">
						<span className="rowGrayChip">
							<i className="icons8-font icons8-location-marker mr-1" />
							{renderWithHighlight(
								highlight,
								'locationName',
								nullSafeGet('location.name', asset),
								false,
								'highlights'
							)}
						</span>
						{asset.nextScheduledTransfer ? (
							<div className="flex flex-shrink flex-row items-center gap-2">
								<ArrowRightOutlined />
								<span className="rowGrayChip">
									<i
										className={`icons8-font ${
											nullSafeGet('nextScheduledTransfer.state.location.name', asset)
												? 'icons8-location-marker'
												: 'icons8-warehouse-3'
										} mr-1`}
									/>
									<span>
										{nullSafeGetOrElse(
											'nextScheduledTransfer.state.location.name',
											asset,
											nullSafeGet('nextScheduledTransfer.state.stockLocation.name', asset)
										)}
									</span>
								</span>
								<span className="bodyregular ">
									Transfer Scheduled:{' '}
									<DateTimeHover
										showDate
										showTime
										timestamp={nullSafeGet('nextScheduledTransfer.triggerAt', asset)}
										timeZone={nullSafeGetOrElse(
											'nextScheduledTransfer.state.location.timeZoneId',
											asset,
											nullSafeGet('nextScheduledTransfer.state.stockLocation.timeZoneId', asset)
										)}
									/>
								</span>
							</div>
						) : null}
					</div>
				</div>
			</div>
			<div className="grid">
				<div className="col-start-1 row-start-1 flex flex-row items-center justify-self-end transition-opacity group-hover:pointer-events-none group-hover:opacity-0">
					<div className="flex flex-col items-end gap-3">
						{asset.assetNumber ? (
							<div className="flex text-gray-500 ">
								Asset Number{' '}
								{renderWithHighlight(
									highlight,
									'assetNumber',
									asset.assetNumber,
									false,
									'highlights'
								)}
							</div>
						) : (
							<div className="flex text-gray-500 opacity-0">No</div>
						)}
						<div className="uppercase text-gray-400">
							{renderWithHighlight(
								highlight,
								'assetTypeName',
								nullSafeGet('assetType.name', asset),
								false,
								'highlights'
							)}
						</div>
						<div className="flex flex-row items-center gap-2">
							{effectiveAge ? (
								<span className="whitespace-nowrap text-gray-500">Age {effectiveAge}</span>
							) : null}
							<EmptorTag className="assetRowDisplay__pill">
								{asset.totalNotClosedWOCount > 0
									? asset.totalNotClosedWOCount + ' Open WOs'
									: 'No Open WOs'}
							</EmptorTag>
						</div>
					</div>
					<div className="ml-5">
						{removeAssetNumberToQRCodeAssociation ? (
							asset.isUserAssignedUUID && asset.assetUUID ? (
								<QRCode size={64} value={asset.assetUUID} />
							) : (
								<HiddenQRCode />
							)
						) : asset.assetUUID ? (
							<QRCode size={64} value={asset.assetUUID} />
						) : (
							<HiddenQRCode />
						)}
					</div>
				</div>
				{!disableActions ? (
					<div className="no-wrap pointer-events-none col-start-1 row-start-1 flex flex-row items-center justify-self-end opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
						<div className="flex flex-row items-center">
							<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_ASSETS}>
								<OWButtonWithSupplierManagedLock
									type="ghost"
									onClick={redirectTo(`/${userType}/assets/edit/${asset.id}`)}
								>
									Edit
								</OWButtonWithSupplierManagedLock>
							</AccessPermissionChecker>
							{parentAssetId ? null : (
								<Button
									className="ml-5"
									type="primary"
									onClick={redirectTo(`/${userType}/assets/${asset.id}/${newWorkOrderLink}`)}
								>
									Request Service
								</Button>
							)}
						</div>
						<HiddenQRCode />
					</div>
				) : null}
			</div>
		</div>
	);
};

const mapStateToProps = (state) => ({
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const ComponentWithRouter = withRouter<any, any>(connect(mapStateToProps)(AssetWithQRCodeDisplay));

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithRouter);
